import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import useGlobalKeyHandler from './../../../hooks/useGlobalKeyHandler';

export interface TabDefinition {
  id: number;
  label: string;
  title: string;
}

export interface TabProps {
  tabs: TabDefinition[];
  'aria-label'?: string;
  /* default selected when not touched */
  defaultSelection?: number;
  /* override selected. Useful when jumping between routes */
  overrideSelection?: number;
  handleTabOnSelect: (n: number) => void;
}

const Tabs: FC<TabProps> = (props) => {
  const [selected, setSelected] = useState<number | undefined>();
  const [touched, setTouched] = useState<boolean>(false);

  useEffect(() => {
    if (props.overrideSelection !== undefined) {
      props.handleTabOnSelect(props.overrideSelection);
      setSelected(props.overrideSelection);
    } else if (props.defaultSelection !== undefined && !touched) {
      props.handleTabOnSelect(props.defaultSelection);
      setSelected(props.defaultSelection);
    }
    // Only re-sync when the externally controlled selection changes — not on every render.
    // Re-running every render previously called handleTabOnSelect (which writes the URL)
    // continuously, causing render loops and clobbering user clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.overrideSelection, props.defaultSelection]);

  const updateSelected = (n: number) => {
    setSelected(n);
    props.handleTabOnSelect(n);
    setTouched(true);
  };

  const handleKeyDownGlobal = (e: KeyboardEvent) => {
    setTimeout(function () {
      const focusedElement = document.activeElement;
      if (focusedElement instanceof HTMLElement && focusedElement.dataset.tabId) {
        const n = Number(focusedElement.dataset.tabId);
        if (e.key === 'Enter' || e.key === 'Space') {
          updateSelected(n);
        } else if (e.key === 'ArrowRight') {
          const focus = n < props.tabs.length - 1 ? n + 1 : n;
          if (focus > n) {
            const nextMenuElement = focusedElement.parentElement?.parentElement?.nextElementSibling?.firstChild?.firstChild;
            if (nextMenuElement) (nextMenuElement as HTMLElement).focus();
          }
        } else if (e.key === 'ArrowLeft') {
          const focus = n > 0 ? n - 1 : n;
          if (focus < n) {
            const previousMenuElement = focusedElement.parentElement?.parentElement?.previousElementSibling?.firstChild?.firstChild;
            if (previousMenuElement) (previousMenuElement as HTMLElement).focus();
          }
        }
      }
    }, 0);
  };
  useGlobalKeyHandler(handleKeyDownGlobal);

  const handleMouse = (_e: React.MouseEvent<HTMLDivElement>, index: number) => {
    updateSelected(index);
  };
  return (
    <div className="mb-4">
      <nav aria-label={props['aria-label']}>
        <ul className="flex flex-wrap list-none p-0 m-[5px_0] border-b border-in-black-25">
          {props.tabs.map((t, i) => (
            <li key={i} className="m-0 cursor-pointer">
              <div className="flex whitespace-nowrap">
                <div
                  className={classNames('px-0 py-1 border-b-4 border-solid', selected === i ? 'border-in-hyperlink-blue' : 'border-white')}
                  data-tab-id={i}
                  onClick={(_) => handleMouse(_, i)}
                  title={t.title}
                  tabIndex={0}
                >
                  {t.label}
                </div>
                <div className="pl-[15px]"></div>
              </div>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

export { Tabs };
